import React from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Print as PrintIcon,
  Download,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Person,
  FamilyRestroom
} from '@mui/icons-material';
import { format } from 'date-fns';
import jsPDF from 'jspdf';

interface ContractPrintData {
  success: boolean;
  data?: {
    member: {
      id: string;
      name: string;
      membershipType: string;
      email: string;
      phone: any;
      address: any;
      contractPeriod: {
        start: string;
        end: string;
      };
      monthlyRate: number;
    };
    checkInPeriod: {
      start: Date;
      end: Date;
    };
    stats: {
      totalCheckIns: number;
      uniqueDays: number;
      deviceBreakdown: Record<string, number>;
      statusBreakdown: Record<string, number>;
      firstCheckIn: Date;
      lastCheckIn: Date;
    };
    contractCompliance: {
      membershipValid: number;
      paymentValid: number;
      contractValid: number;
      successfulCheckIns: number;
      complianceRate: string;
    };
    familyStats?: {
      totalFamilyCheckIns: number;
      familyMembers: string[];
      relationshipBreakdown: Record<string, number>;
    } | null;
    recentCheckIns: Array<{
      timestamp: Date;
      status: string;
      deviceType: string;
      familyMember: string | null;
      relationship: string | null;
    }>;
  };
  message?: string;
}

interface ContractPrintDialogProps {
  open: boolean;
  onClose: () => void;
  contractData: ContractPrintData | null;
  loading: boolean;
}

const ContractPrintDialog: React.FC<ContractPrintDialogProps> = ({
  open,
  onClose,
  contractData,
  loading
}) => {
  const generateContractUsagePDF = () => {
    if (!contractData?.data) return;

    const doc = new jsPDF();
    const { member, stats, contractCompliance, familyStats, checkInPeriod } = contractData.data;
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(44, 62, 80);
    doc.text('CONTRACT USAGE REPORT', 20, 30);
    
    // Member Info
    doc.setFontSize(14);
    doc.text('Member Information', 20, 50);
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    
    let yPos = 60;
    const memberInfo = [
      `Name: ${member.name}`,
      `Membership Type: ${member.membershipType}`,
      `Email: ${member.email}`,
      `Monthly Rate: R ${member.monthlyRate}`,
      `Contract Period: ${format(new Date(member.contractPeriod.start), 'dd/MM/yyyy')} - ${format(new Date(member.contractPeriod.end), 'dd/MM/yyyy')}`,
      `Report Period: ${format(checkInPeriod.start, 'dd/MM/yyyy')} - ${format(checkInPeriod.end, 'dd/MM/yyyy')}`
    ];
    
    memberInfo.forEach(info => {
      doc.text(info, 20, yPos);
      yPos += 7;
    });

    // Usage Statistics
    yPos += 10;
    doc.setFontSize(14);
    doc.setTextColor(44, 62, 80);
    doc.text('Usage Statistics', 20, yPos);
    
    yPos += 10;
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    
    const usageStats = [
      `Total Check-ins: ${stats.totalCheckIns}`,
      `Unique Days Visited: ${stats.uniqueDays}`,
      `Success Rate: ${contractCompliance.complianceRate}%`,
      `Successful Check-ins: ${contractCompliance.successfulCheckIns}`,
      `First Check-in: ${format(stats.firstCheckIn, 'dd/MM/yyyy HH:mm')}`,
      `Last Check-in: ${format(stats.lastCheckIn, 'dd/MM/yyyy HH:mm')}`
    ];
    
    usageStats.forEach(stat => {
      doc.text(stat, 20, yPos);
      yPos += 7;
    });

    // Device Usage
    yPos += 10;
    doc.setFontSize(12);
    doc.setTextColor(44, 62, 80);
    doc.text('Device Usage Breakdown', 20, yPos);
    
    yPos += 10;
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    
    Object.entries(stats.deviceBreakdown).forEach(([device, count]) => {
      doc.text(`${device.charAt(0).toUpperCase() + device.slice(1)}: ${count} check-ins`, 20, yPos);
      yPos += 7;
    });

    // Family Usage (if applicable)
    if (familyStats) {
      yPos += 10;
      doc.setFontSize(12);
      doc.setTextColor(44, 62, 80);
      doc.text('Family Usage', 20, yPos);
      
      yPos += 10;
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      
      doc.text(`Total Family Check-ins: ${familyStats.totalFamilyCheckIns}`, 20, yPos);
      yPos += 7;
      doc.text(`Family Members: ${familyStats.familyMembers.join(', ')}`, 20, yPos);
      yPos += 7;
      
      Object.entries(familyStats.relationshipBreakdown).forEach(([relationship, count]) => {
        doc.text(`${relationship}: ${count} check-ins`, 20, yPos);
        yPos += 7;
      });
    }

    return doc;
  };

  const handlePrint = () => {
    const doc = generateContractUsagePDF();
    if (doc) {
      doc.autoPrint();
      window.open(doc.output('bloburl'), '_blank');
    }
  };

  const handleDownload = () => {
    const doc = generateContractUsagePDF();
    if (doc && contractData?.data) {
      doc.save(`${contractData.data.member.name}_Contract_Usage_Report.pdf`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'contract-expired': return 'error';
      case 'payment-overdue': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle />;
      case 'contract-expired': return <ErrorIcon />;
      case 'payment-overdue': return <Warning />;
      case 'suspended': return <ErrorIcon />;
      default: return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Contract Usage Report
        {contractData?.data?.member && (
          <Typography variant="subtitle1" color="text.secondary">
            {contractData.data.member.name} - {contractData.data.member.membershipType}
          </Typography>
        )}
      </DialogTitle>
      
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <Typography>Loading contract data...</Typography>
          </Box>
        )}
        
        {contractData && !contractData.success && (
          <Box p={2}>
            <Typography color="error">{contractData.message}</Typography>
          </Box>
        )}
        
        {contractData?.data && (
          <Box>
            {/* Statistics Overview */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="primary">
                    {contractData.data.stats.totalCheckIns}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Check-ins
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="primary">
                    {contractData.data.stats.uniqueDays}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unique Days
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="success.main">
                    {contractData.data.contractCompliance.complianceRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Success Rate
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="primary">
                    {contractData.data.contractCompliance.successfulCheckIns}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Successful
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {/* Device Usage */}
            <Typography variant="h6" gutterBottom>Device Usage</Typography>
            <Box sx={{ mb: 2 }}>
              {Object.entries(contractData.data.stats.deviceBreakdown).map(([device, count]) => (
                <Chip
                  key={device}
                  label={`${device}: ${count}`}
                  sx={{ mr: 1, mb: 1 }}
                  variant="outlined"
                />
              ))}
            </Box>

            {/* Family Stats */}
            {contractData.data.familyStats && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  <FamilyRestroom sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Family Usage
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Total Family Check-ins: {contractData.data.familyStats.totalFamilyCheckIns}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Family Members: {contractData.data.familyStats.familyMembers.join(', ')}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {Object.entries(contractData.data.familyStats.relationshipBreakdown).map(([relationship, count]) => (
                    <Chip
                      key={relationship}
                      label={`${relationship}: ${count}`}
                      sx={{ mr: 1, mb: 1 }}
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </>
            )}

            <Divider sx={{ mb: 2 }} />

            {/* Recent Check-ins */}
            <Typography variant="h6" gutterBottom>Recent Check-ins</Typography>
            <List dense>
              {contractData.data.recentCheckIns.slice(0, 5).map((checkIn, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    {checkIn.familyMember ? <FamilyRestroom /> : <Person />}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">
                          {format(new Date(checkIn.timestamp), 'dd/MM/yyyy HH:mm')}
                        </Typography>
                        <Chip
                          size="small"
                          label={checkIn.status}
                          color={getStatusColor(checkIn.status) as any}
                          {...(getStatusIcon(checkIn.status) && { icon: getStatusIcon(checkIn.status)! })}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {checkIn.deviceType}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      checkIn.familyMember && (
                        <Typography variant="caption">
                          {checkIn.familyMember} ({checkIn.relationship})
                        </Typography>
                      )
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {contractData?.data && (
          <>
            <Button
              startIcon={<Download />}
              onClick={handleDownload}
              variant="outlined"
            >
              Download
            </Button>
            <Button
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              variant="contained"
            >
              Print Report
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContractPrintDialog;
